import { HandCoins, Plus } from 'lucide-react';
import { Link } from 'react-router';
import { useMesPretsAccordes, useParametresPrets, usePrets } from '@/api/hooks';
import { LIBELLE_ETAPE } from '@/api/types';
import { Chargement, ErreurBloc } from '@/components/common';
import { montantDevise, StatutPretAccordeBadge, StatutPretBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr } from '@/lib/utils';

/** Onglet « Mes prêts sociaux » de l'espace personnel (§ 5.11) : éligibilité, demande en cours, plafond, historique. */
export function MesPrets() {
  const params = useParametresPrets();
  const prets = usePrets({ onglet: 'mes' });
  const accordes = useMesPretsAccordes();
  if (params.isLoading || prets.isLoading) return <Chargement />;
  if (params.error || !params.data) return <ErreurBloc erreur={params.error} />;
  const p = params.data;
  const liste = prets.data ?? [];
  const enCours = liste.find((d) => !['REJETE', 'ANNULE', 'SOLDE'].includes(d.statut));
  return (
    <div className="space-y-4">
      <Card data-testid="mes-prets">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <HandCoins className="size-5" /> Mes prêts sociaux
            </span>
            <Button asChild disabled={!p.eligibilite.eligible}>
              <Link to="/rh/prets/nouvelle">
                <Plus className="size-4" /> Demander un prêt
              </Link>
            </Button>
          </CardTitle>
          <CardDescription>
            {p.eligibilite.eligible ? `Éligible (${p.ancienneteMinimaleMois} mois de service au moins). ` : `${p.eligibilite.motif} `}
            Plafond : {p.quotiteDefaut} % du décompte final estimé, moins vos engagements en cours, sur le total à rembourser ; mensualités limitées à {p.seuilCapacite} % du salaire net ; {p.tauxAnnuel.CONSOMMATION} % l'an, {p.dureeMaxMois} mois au plus, remboursement un mois après le décaissement.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {enCours ? (
            <div className="rounded-lg border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Link to={`/rh/prets/${enCours.id}`} className="font-medium text-primary hover:underline">
                    {enCours.numero ?? 'Demande en cours'}
                  </Link>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {enCours.libelleNature} · {montantDevise(enCours.montantConfirme ?? enCours.montantSouhaite, enCours.devise)}
                    {enCours.dureeMois ? ` sur ${enCours.dureeMois} mois` : ''}
                  </span>
                </div>
                <div className="space-x-1">
                  <StatutPretBadge statut={enCours.statut} />
                  {enCours.statut === 'SOUMIS' && enCours.etapeCourante && <Badge variant="secondary">{enCours.conditionsEnAttente ? 'Conditions à accepter' : LIBELLE_ETAPE[enCours.etapeCourante]}</Badge>}
                </div>
              </div>
              {enCours.plafond && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Plafond communiqué : <span className="font-medium text-foreground">{montantDevise(enCours.plafond.plafondDisponible, enCours.devise)}</span> (total à rembourser), valable jusqu'au {dateFr(enCours.plafond.valideJusquAu)}
                  {enCours.plafond.expire ? ' · expiré, nouveau calcul à demander aux RH' : ''} ; la communication du plafond n'est pas une décision d'octroi.
                </p>
              )}
              {enCours.statut === 'PLAFOND_DEMANDE' && <p className="mt-2 text-sm text-muted-foreground">Les Ressources humaines calculent votre décompte final et votre plafond.</p>}
              {(enCours.statut === 'PLAFOND_COMMUNIQUE' || enCours.statut === 'SIMULATION') && <p className="mt-2 text-sm text-muted-foreground">Comparez des simulations puis confirmez votre dossier depuis la fiche.</p>}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucune demande de prêt en cours.</p>
          )}
          {(accordes.data ?? []).length > 0 && (
            <div className="space-y-2" data-testid="mes-prets-accordes">
              <h3 className="text-sm font-medium">Mes prêts accordés</h3>
              {(accordes.data ?? []).map((p) => (
                <div key={p.id} className="rounded-lg border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={p.demandeId ? `/rh/prets/${p.demandeId}?onglet=remboursement` : `/rh/portefeuille/${p.id}`} className="font-medium text-primary hover:underline">
                      {p.numero}
                    </Link>
                    <StatutPretAccordeBadge statut={p.statut} />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {montantDevise(p.montantTotal, p.devise)} sur {p.dureeMois} mois · mensualité {montantDevise(p.mensualite, p.devise)}
                    {p.dateDecaissement ? ` · décaissé le ${dateFr(p.dateDecaissement)}` : ' · en attente de décaissement'} · capital restant dû <span className="font-medium text-foreground">{montantDevise(p.solde.capitalRestant, p.devise)}</span>
                    {p.solde.prochaineEcheance ? ` · prochaine échéance le ${dateFr(p.solde.prochaineEcheance.date)} (${montantDevise(p.solde.prochaineEcheance.mensualite, p.devise)})` : ''}
                    {p.solde.nonRetenues ? ` · ${p.solde.nonRetenues} échéance(s) non retenue(s)` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
          {liste.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Nature</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="hidden sm:table-cell">Déposée le</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {liste.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Link to={`/rh/prets/${d.id}`} className="font-medium text-primary hover:underline">
                        {d.numero ?? 'Demande'}
                      </Link>
                    </TableCell>
                    <TableCell>{d.libelleNature}</TableCell>
                    <TableCell className="num text-right">{montantDevise(d.montantConfirme ?? d.montantSouhaite, d.devise)}</TableCell>
                    <TableCell className="hidden sm:table-cell">{dateFr(d.dateDepot)}</TableCell>
                    <TableCell>
                      <StatutPretBadge statut={d.statut} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
